import React, { useEffect, useRef, useState } from 'react';
import { Linking, Pressable, Text, TextInput, View } from 'react-native';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  OperationalFilterChipGroup,
  OperationalFilterFieldLabel,
  OperationalFilterSectionDivider,
  OperationalFilterSegmentedRow,
} from '../../../shared/filters/OperationalFilterPrimitives';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import type { BranchSettingsScreenViewModel } from '../useBranchSettingsScreen';
import {
  PRINT_AGENT_DOWNLOAD_URL,
  compareAgentVersions,
  fetchLatestAgentVersion,
  fetchPrintAgentHealth,
  fetchPrintAgentPrinters,
  formatLastPrintAgo,
  getLastPrintStatus,
  isAllowedPrintAgentUrl,
  launchPrintAgent,
  resolveAgentStatus,
  webAgentPrintTransport,
  type AgentPrinter,
  type LastPrintStatus,
  type PrintAgentHealth,
  type ReceiptPrintMode,
} from '../../../printing/public-api';
import type { ReceiptWidthMm } from '@vuelto/print';
import { SettingsSaveCta, StatusBadge, SettingsNotice, hubStyles } from './settingsHubUi';

/**
 * Tickets de venta — estados semánticos del agente local:
 *  - running:     badge verde + meta de último job + prueba
 *  - unreachable: badge ámbar + "Abrir programa" (vuelto-agent://) + descarga
 * El browser no distingue "instalado apagado" de "no instalado" — se
 * ofrecen ambas acciones con copy honesto.
 */
const DEFAULT_AGENT_URL = 'http://127.0.0.1:9310';

export function PrintConfigCard({ vm, layoutWide }: { vm: BranchSettingsScreenViewModel; layoutWide: boolean }) {
  const printModeUi: ReceiptPrintMode = vm.printConfig.mode;
  const [agentHealth, setAgentHealth] = useState<PrintAgentHealth | null>(null);
  const [agentPrinters, setAgentPrinters] = useState<AgentPrinter[]>([]);
  const [agentProbing, setAgentProbing] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [printerDraft, setPrinterDraft] = useState(vm.printConfig.printerName);
  const [agentUrlDraft, setAgentUrlDraft] = useState(vm.printConfig.agentUrl);
  const [lastStatus, setLastStatus] = useState<LastPrintStatus | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [launchHint, setLaunchHint] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    () => !!vm.printConfig.agentUrl && vm.printConfig.agentUrl !== DEFAULT_AGENT_URL,
  );
  const launchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setPrinterDraft(vm.printConfig.printerName);
    setAgentUrlDraft(vm.printConfig.agentUrl);
  }, [vm.printConfig.printerName, vm.printConfig.agentUrl, vm.currentBranch?.id]);

  useEffect(() => {
    let alive = true;
    if (!vm.currentBranchId) return undefined;
    void getLastPrintStatus(vm.currentBranchId).then((s) => {
      if (alive) setLastStatus(s);
    });
    return () => {
      alive = false;
    };
  }, [vm.currentBranchId]);

  useEffect(() => {
    if (!agentHealth?.ok) return undefined;
    let alive = true;
    void fetchLatestAgentVersion().then((v) => {
      if (alive) setLatestVersion(v);
    });
    return () => {
      alive = false;
    };
  }, [agentHealth?.ok]);

  useEffect(
    () => () => {
      if (launchTimer.current) clearTimeout(launchTimer.current);
    },
    [],
  );

  const probeAgent = async () => {
    if (agentProbing) return;
    setAgentProbing(true);
    setAgentHealth(null);
    setAgentPrinters([]);
    try {
      const draftConfig = { ...vm.printConfig, agentUrl: agentUrlCandidate };
      if (!isAllowedPrintAgentUrl(draftConfig.agentUrl)) {
        setAgentHealth({ ok: false, error: 'invalid_url' });
        showError(
          'Dirección inválida',
          'Usá la dirección local (127.0.0.1) o la IP de una computadora del local.',
        );
        return;
      }
      if (draftConfig.agentUrl !== vm.printConfig.agentUrl) {
        await vm.handleSavePrintConfig({ agentUrl: draftConfig.agentUrl });
      }
      const health = await fetchPrintAgentHealth(draftConfig);
      setAgentHealth(health);
      if (health.ok) {
        const res = await fetchPrintAgentPrinters(draftConfig);
        setAgentPrinters(res.ok ? res.printers : []);
      }
    } finally {
      setAgentProbing(false);
    }
  };

  useEffect(() => {
    if (vm.printConfig.mode !== 'agent') return;
    void probeAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vm.printConfig.mode, vm.printConfig.agentUrl, vm.currentBranch?.id]);

  const printerName = printerDraft.trim();
  const printerDirty = printerName !== vm.printConfig.printerName;
  const agentUrlCandidate = agentUrlDraft.trim() || DEFAULT_AGENT_URL;
  const agentUrlDirty = agentUrlCandidate !== vm.printConfig.agentUrl;

  const savePrinter = () => {
    if (!printerDirty) return;
    void vm.handleSavePrintConfig({ printerName });
  };

  const saveAgentUrl = () => {
    if (!agentUrlDirty) return;
    if (!isAllowedPrintAgentUrl(agentUrlCandidate)) {
      showError(
        'Dirección inválida',
        'Usá la dirección local (127.0.0.1) o la IP de una computadora del local.',
      );
      return;
    }
    void vm.handleSavePrintConfig({ agentUrl: agentUrlCandidate });
  };

  const handleTestPrint = async () => {
    if (testBusy) return;
    setTestBusy(true);
    try {
      const printer = printerName || vm.printConfig.printerName;
      const url = vm.printConfig.agentUrl || DEFAULT_AGENT_URL;
      if (printer !== vm.printConfig.printerName) {
        await vm.handleSavePrintConfig({ printerName: printer });
      }
      await webAgentPrintTransport({
        agentUrl: url,
        printerName: printer,
        document: {
          text: 'PRUEBA DE IMPRESIÓN\nVuelto\nSi podés leer este ticket y el papel se cortó correctamente, la impresora está lista.',
          html: '',
        },
      });
      showSuccess('Prueba enviada', 'Revisá si el ticket salió correctamente.');
    } catch (e: unknown) {
      showError('No pudimos imprimir la prueba', e instanceof Error ? e.message : 'Revisá la conexión con el programa y la impresora.');
    } finally {
      setTestBusy(false);
    }
  };

  const handleLaunchAgent = async () => {
    try {
      await launchPrintAgent();
    } catch {
      setLaunchHint(true);
    }
    // El agente tarda ~1-2 s en levantar → re-probar solo.
    launchTimer.current = setTimeout(() => void probeAgent(), 4000);
  };

  const status = resolveAgentStatus({ probing: agentProbing, health: agentHealth });
  const updateAvailable =
    agentHealth?.ok && agentHealth.version && latestVersion
      ? compareAgentVersions(agentHealth.version, latestVersion) < 0
      : false;
  const lastWasFallback = lastStatus?.fallbackWarning != null;

  const modeHint =
    printModeUi === 'off'
      ? 'El ticket de venta no se imprime al cobrar; podés imprimirlo más tarde desde Ventas. Comandas y precuenta tienen su propia configuración y se siguen imprimiendo.'
      : printModeUi === 'agent'
        ? 'El ticket sale directo por la térmica. Necesitás el programa de impresión en una computadora del local.'
        : 'Abrimos el ticket antes de imprimir para que elijas una impresora.';

  return (
    <PaperSheet padded testID="branch.print.config">
      <View style={hubStyles.cardTitleRow}>
        <Text style={styles.cardTitle}>Tickets de venta</Text>
        {printModeUi === 'agent' ? (
          <StatusBadge
            tone={status === 'running' ? 'ok' : status === 'probing' ? 'muted' : 'warn'}
            label={
              status === 'running'
                ? `Conectado${agentHealth?.version ? ` · v${agentHealth.version}` : ''}`
                : status === 'probing'
                  ? 'Comprobando…'
                  : 'Sin conexión'
            }
            testID="branch.print.agent-status"
          />
        ) : null}
      </View>

      {lastWasFallback && printModeUi === 'agent' ? (
        <SettingsNotice tone="warn" testID="branch.print.last-fallback">
          El último ticket se abrió en vista previa porque el programa de impresión no estaba disponible
          {lastStatus ? ` (${formatLastPrintAgo(lastStatus.at)})` : ''}.
        </SettingsNotice>
      ) : null}

      <View style={hubStyles.controlBlock}>
        <OperationalFilterFieldLabel>Cómo imprimir</OperationalFilterFieldLabel>
        <OperationalFilterChipGroup
          ticketTone
          layout="hug"
          options={[
            { value: 'off', label: 'No imprimir', accessibilityLabel: 'No imprimir automáticamente' },
            { value: 'browser', label: 'Vista previa', accessibilityLabel: 'Imprimir con vista previa' },
            { value: 'agent', label: 'Impresión directa', accessibilityLabel: 'Imprimir directamente con el programa' },
          ]}
          value={printModeUi}
          disabled={!vm.canEditPosRules}
          onChange={(id) => {
            void vm.handleSavePrintConfig({ mode: id as ReceiptPrintMode });
          }}
        />
        <Text style={hubStyles.hintSecondary}>{modeHint}</Text>
        <Text style={hubStyles.hintSecondary}>
          Esta configuración es de este dispositivo. Si usás otra caja, ajustala ahí.
        </Text>
      </View>

      {printModeUi === 'agent' ? (
        <>
          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Programa de impresión</OperationalFilterFieldLabel>
            <Text style={hubStyles.hintSecondary}>
              {status === 'running'
                ? 'Listo para imprimir' +
                  (agentHealth?.watchdog ? ' · inicio automático' : '') +
                  (agentHealth?.lastPrintAt
                    ? ` · última impresión ${formatLastPrintAgo(agentHealth.lastPrintAt)}${agentHealth.lastPrintOk === false ? ' (falló)' : ''}`
                    : '')
                : status === 'probing'
                  ? 'Comprobando la conexión con el programa…'
                  : 'No pudimos conectarnos con el programa de impresión. Abrilo en la computadora conectada a la impresora y volvé a comprobar.'}
            </Text>
            {updateAvailable ? (
              <>
                <SettingsNotice tone="warn">
                  Hay una nueva versión del programa de impresión (v{latestVersion}).
                </SettingsNotice>
                <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <SettingsSaveCta
                    label="Descargar actualización"
                    layoutWide={false}
                    variant="primary"
                    onPress={() => void Linking.openURL(PRINT_AGENT_DOWNLOAD_URL)}
                    testID="branch.print.agent-download-update"
                  />
                </View>
              </>
            ) : null}
            {status === 'unreachable' ? (
              <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
                <SettingsSaveCta
                  label="Abrir programa"
                  layoutWide={false}
                  variant="primary"
                  onPress={() => void handleLaunchAgent()}
                  testID="branch.print.agent-launch"
                />
                <SettingsSaveCta
                  label="Descargar para computadora"
                  layoutWide={false}
                  variant="secondary"
                  onPress={() => void Linking.openURL(PRINT_AGENT_DOWNLOAD_URL)}
                  testID="branch.print.agent-download"
                />
                <SettingsSaveCta
                  label="Comprobar conexión"
                  layoutWide={false}
                  variant="secondary"
                  onPress={() => void probeAgent()}
                  disabled={agentProbing}
                  testID="branch.print.agent-probe"
                />
              </View>
            ) : (
              <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
                <SettingsSaveCta
                  label={agentProbing ? 'Comprobando…' : 'Comprobar conexión'}
                  layoutWide={false}
                  variant="secondary"
                  onPress={() => void probeAgent()}
                  disabled={agentProbing}
                  testID="branch.print.agent-probe"
                />
              </View>
            )}
            {launchHint ? (
              <Text style={hubStyles.hintMuted}>
                Si el programa no se abrió, descargá la versión más reciente.
              </Text>
            ) : null}

            <Pressable
              onPress={() => setShowAdvanced((v) => !v)}
              testID="branch.print.advanced-toggle"
              accessibilityRole="button"
            >
              <Text style={hubStyles.hintSecondary}>
                {showAdvanced ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas'}
              </Text>
            </Pressable>
            {showAdvanced ? (
              <>
                <OperationalFilterFieldLabel>Dirección del programa</OperationalFilterFieldLabel>
                <Text style={hubStyles.hintMuted}>
                  Ingresá la dirección de la computadora donde está instalado el programa de impresión.
                </Text>
                <TextInput
                  style={hubStyles.inputTicket}
                  value={agentUrlDraft}
                  onChangeText={setAgentUrlDraft}
                  placeholder={DEFAULT_AGENT_URL}
                  placeholderTextColor={ticketColors.ink3}
                  editable={vm.canEditPosRules && !vm.printConfigSaving}
                  autoCapitalize="none"
                  testID="branch.print.agent-url"
                />
                <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <SettingsSaveCta
                    label="Guardar dirección"
                    layoutWide={false}
                    variant="secondary"
                    onPress={saveAgentUrl}
                    disabled={!agentUrlDirty || vm.printConfigSaving}
                    testID="branch.print.agent-url-save"
                  />
                </View>
              </>
            ) : null}
          </View>

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Impresora</OperationalFilterFieldLabel>
            {agentHealth?.ok && agentPrinters.length > 0 ? (
              <OperationalFilterChipGroup
                ticketTone
                layout="hug"
                options={agentPrinters.map((p) => ({
                  value: p.name,
                  label: p.name,
                  accessibilityLabel: `Impresora ${p.name}`,
                }))}
                value={printerName}
                disabled={!vm.canEditPosRules}
                onChange={(id) => setPrinterDraft(id)}
              />
            ) : null}
            <Text style={hubStyles.hintMuted}>
              {agentHealth?.ok
                ? agentPrinters.length > 0
                  ? 'Elegí la impresora que querés usar.'
                  : 'No encontramos impresoras disponibles. Instalá una en la computadora o ingresá su nombre manualmente.'
                : 'Conectate al programa para ver las impresoras disponibles. También podés ingresar el nombre manualmente.'}
            </Text>
            <Text style={hubStyles.hintSecondary}>Nombre o dirección de la impresora</Text>
            <TextInput
              style={hubStyles.inputTicket}
              value={printerDraft}
              onChangeText={setPrinterDraft}
              placeholder="Ej.: XP-80C o tcp:192.168.1.50:9100"
              placeholderTextColor={ticketColors.ink3}
              editable={vm.canEditPosRules && !vm.printConfigSaving}
              autoCapitalize="none"
              testID="branch.print.printer-name"
            />
            <View style={{ flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap' }}>
              {vm.canEditPosRules ? (
                <SettingsSaveCta
                  label="Guardar impresora"
                  layoutWide={false}
                  variant="primary"
                  onPress={savePrinter}
                  disabled={!printerDirty || vm.printConfigSaving}
                  testID="branch.print.printer-save"
                />
              ) : null}
              <SettingsSaveCta
                label={testBusy ? 'Imprimiendo…' : 'Imprimir prueba'}
                layoutWide={false}
                variant="secondary"
                onPress={() => void handleTestPrint()}
                disabled={testBusy || !agentHealth?.ok || !printerName}
                testID="branch.print.test"
              />
            </View>
            <Text style={hubStyles.hintMuted}>
              {!agentHealth?.ok
                ? 'Conectá el programa de impresión para hacer una prueba.'
                : !printerName
                  ? 'Elegí o ingresá una impresora para hacer una prueba.'
                  : 'Imprimí una prueba para comprobar el papel y el corte.'}
            </Text>
          </View>
        </>
      ) : null}

      {printModeUi !== 'off' ? (
        <>
          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Ancho del rollo</OperationalFilterFieldLabel>
            <OperationalFilterChipGroup
              ticketTone
              layout="hug"
              options={[
                { value: '58', label: '58 mm', accessibilityLabel: '58 mm' },
                { value: '72', label: '72 mm', accessibilityLabel: '72 mm' },
                { value: '80', label: '80 mm', accessibilityLabel: '80 mm' },
              ]}
              value={String(vm.printConfig.widthMm)}
              disabled={!vm.canEditPosRules}
              onChange={(id) => {
                void vm.handleSavePrintConfig({ widthMm: Number(id) as ReceiptWidthMm });
              }}
            />
          </View>

          <OperationalFilterSectionDivider />

          <View style={hubStyles.controlBlock}>
            <OperationalFilterFieldLabel>Impresión al cobrar</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              ticketTone
              layout="hug"
              options={[
                { value: 'off', label: 'Manual', accessibilityLabel: 'Imprimir ticket manual' },
                { value: 'on', label: 'Automático', accessibilityLabel: 'Imprimir ticket automático' },
              ]}
              value={vm.printConfig.autoPrint ? 'on' : 'off'}
              onChange={(v) => {
                if (!vm.canEditPosRules || vm.printConfigSaving) return;
                void vm.handleSavePrintConfig({ autoPrint: v === 'on' });
              }}
            />
            <Text style={hubStyles.hintSecondary}>
              En Automática, el ticket se imprime al finalizar cada venta. En Manual, podés imprimirlo desde el aviso o desde Ventas.
            </Text>
          </View>
        </>
      ) : null}
    </PaperSheet>
  );
}
